import {
  BILT_ACCENT_DEFAULT,
  BILT_MARK_MONO_SRC,
  formatBiltBuildUrlLabel,
} from './biltBrandDisplay';

export type IdentityCardFormat = 'placard' | 'windshield' | 'story';

export interface IdentityCardInput {
  vehicleLabel: string;
  ownerLabel: string;
  profileUrl: string;
  qrImageUrl: string;
}

const FORMATS: Record<
  IdentityCardFormat,
  { width: number; height: number; label: string }
> = {
  placard: { width: 1200, height: 1600, label: 'show-placard' },
  windshield: { width: 1400, height: 600, label: 'windshield-card' },
  story: { width: 1080, height: 1920, label: 'social-story' },
};

const COLORS = {
  bg: '#0a0a0b',
  border: 'rgba(255,255,255,0.12)',
  text: '#f4f4f5',
  muted: '#a1a1aa',
  dim: '#71717a',
  accent: BILT_ACCENT_DEFAULT,
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function loadImageSafe(src: string): Promise<HTMLImageElement | null> {
  return loadImage(src).catch(() => null);
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
      if (lines.length >= maxLines) break;
    } else {
      current = test;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  return lines;
}

const TRIM_INSET = 48;
const CROP_LEN = 32;

function drawCropMarks(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const t = TRIM_INSET;
  const len = CROP_LEN;
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 2;

  const corners: Array<[number, number, number, number, number, number]> = [
    [t, t, t + len, t, t, t + len],
    [width - t, t, width - t - len, t, width - t, t + len],
    [t, height - t, t + len, height - t, t, height - t - len],
    [width - t, height - t, width - t - len, height - t, width - t, height - t - len],
  ];

  for (const [x1, y1, x2, y2, x3, y3] of corners) {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x3, y3);
    ctx.stroke();
  }

  ctx.setLineDash([8, 8]);
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 1;
  ctx.strokeRect(t, t, width - t * 2, height - t * 2);
  ctx.restore();
}

function drawTintedMark(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null,
  x: number,
  y: number,
  size: number,
  ink: string
) {
  if (!mark || mark.naturalWidth <= 0) return false;
  const off = document.createElement('canvas');
  off.width = size;
  off.height = size;
  const octx = off.getContext('2d');
  if (!octx) {
    ctx.drawImage(mark, x, y, size, size);
    return true;
  }
  octx.fillStyle = ink;
  octx.fillRect(0, 0, size, size);
  octx.globalCompositeOperation = 'destination-in';
  octx.drawImage(mark, 0, 0, size, size);
  ctx.drawImage(off, x, y);
  return true;
}

function drawBrandLockup(
  ctx: CanvasRenderingContext2D,
  mark: HTMLImageElement | null,
  x: number,
  y: number,
  markSize: number,
  textSize: number
) {
  ctx.save();
  const hasMark = drawTintedMark(ctx, mark, x, y - markSize + 8, markSize, COLORS.accent);
  ctx.fillStyle = COLORS.text;
  ctx.font = `600 ${textSize}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText('BILT', x + (hasMark ? markSize + 12 : 0), y);
  ctx.fillStyle = COLORS.dim;
  ctx.font = `500 ${Math.round(textSize * 0.35)}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('VEHICLE IDENTITY', x, y + textSize * 0.55);
  ctx.restore();
}

/**
 * Draw the branded QR preserving aspect so the under-QR build URL is not squashed.
 * @returns drawn height in CSS pixels
 */
function drawQrFrame(
  ctx: CanvasRenderingContext2D,
  qr: HTMLImageElement,
  x: number,
  y: number,
  size: number
): number {
  const pad = 16;
  const srcW = qr.naturalWidth || size;
  const srcH = qr.naturalHeight || size;
  const aspect = srcW > 0 ? srcH / srcW : 1;
  const drawW = size;
  const drawH = Math.round(size * aspect);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x - pad, y - pad, drawW + pad * 2, drawH + pad * 2);
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.strokeRect(x - pad, y - pad, drawW + pad * 2, drawH + pad * 2);
  ctx.drawImage(qr, x, y, drawW, drawH);
  return drawH;
}

function drawCard(
  ctx: CanvasRenderingContext2D,
  format: IdentityCardFormat,
  input: IdentityCardInput,
  qr: HTMLImageElement,
  mark: HTMLImageElement | null
) {
  const { width, height } = FORMATS[format];
  const urlLabel = formatBiltBuildUrlLabel(input.profileUrl);

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.fillStyle = COLORS.accent;
  ctx.fillRect(0, 0, width, 12);
  ctx.fillRect(0, height - 8, width, 8);

  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 3;
  ctx.strokeRect(TRIM_INSET, TRIM_INSET, width - TRIM_INSET * 2, height - TRIM_INSET * 2);

  if (format === 'windshield') {
    const qrSize = 260;
    const leftPad = 80;
    const qrX = width - qrSize - 100;
    const srcW = qr.naturalWidth || qrSize;
    const srcH = qr.naturalHeight || qrSize;
    const qrDrawH = Math.round(qrSize * (srcW > 0 ? srcH / srcW : 1));
    const qrY = Math.max(TRIM_INSET + 24, (height - qrDrawH) / 2);

    drawBrandLockup(ctx, mark, leftPad, 100, 40, 36);

    ctx.textAlign = 'left';
    ctx.fillStyle = COLORS.text;
    ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
    const titleLines = wrapText(ctx, input.vehicleLabel, width - qrSize - 220, 2);
    titleLines.forEach((line, i) => {
      ctx.fillText(line, leftPad, 200 + i * 62);
    });

    ctx.fillStyle = COLORS.muted;
    ctx.font = '28px system-ui, -apple-system, sans-serif';
    ctx.fillText(`Builder · ${input.ownerLabel}`, leftPad, 340);

    ctx.fillStyle = COLORS.dim;
    ctx.font = '22px system-ui, -apple-system, sans-serif';
    ctx.fillText('Scan for full build profile', leftPad, height - 80);
    ctx.fillStyle = COLORS.accent;
    ctx.font = '600 20px system-ui, -apple-system, sans-serif';
    ctx.fillText(urlLabel, leftPad, height - 48);

    drawQrFrame(ctx, qr, qrX, qrY, qrSize);
    drawCropMarks(ctx, width, height);
    return;
  }

  const isStory = format === 'story';
  const margin = isStory ? 72 : 64;
  const qrSize = isStory ? 400 : 360;
  const titleSize = isStory ? 58 : 48;
  const ownerSize = isStory ? 32 : 28;

  drawBrandLockup(ctx, mark, margin, margin + 28, isStory ? 44 : 38, isStory ? 40 : 34);

  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${titleSize}px system-ui, -apple-system, sans-serif`;
  const titleY = isStory ? 300 : 240;
  const titleLines = wrapText(ctx, input.vehicleLabel, width - margin * 2, isStory ? 3 : 2);
  titleLines.forEach((line, i) => {
    ctx.fillText(line, width / 2, titleY + i * (titleSize + 12));
  });

  const ownerY = titleY + titleLines.length * (titleSize + 12) + (isStory ? 36 : 28);
  ctx.font = `${ownerSize}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = COLORS.muted;
  ctx.fillText(`Builder · ${input.ownerLabel}`, width / 2, ownerY);

  const qrY = ownerY + (isStory ? 80 : 64);
  const qrDrawH = drawQrFrame(ctx, qr, (width - qrSize) / 2, qrY, qrSize);

  const footerY = qrY + qrDrawH + (isStory ? 72 : 56);
  ctx.fillStyle = COLORS.dim;
  ctx.font = `${isStory ? 28 : 24}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('Scan for full build profile', width / 2, footerY);

  ctx.fillStyle = COLORS.accent;
  ctx.font = `600 ${isStory ? 24 : 20}px system-ui, -apple-system, sans-serif`;
  ctx.fillText(urlLabel, width / 2, footerY + (isStory ? 40 : 32));

  ctx.strokeStyle = COLORS.border;
  ctx.beginPath();
  ctx.moveTo(margin, height - margin - 48);
  ctx.lineTo(width - margin, height - margin - 48);
  ctx.stroke();

  ctx.fillStyle = COLORS.dim;
  ctx.font = `500 ${isStory ? 22 : 18}px system-ui, -apple-system, sans-serif`;
  ctx.fillText('BILTSPEC.COM', width / 2, height - margin - 12);
  drawCropMarks(ctx, width, height);
}

export async function downloadIdentityCard(format: IdentityCardFormat, input: IdentityCardInput): Promise<void> {
  const spec = FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = spec.width;
  canvas.height = spec.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  const [qr, mark] = await Promise.all([
    loadImage(input.qrImageUrl),
    loadImageSafe(BILT_MARK_MONO_SRC),
  ]);
  drawCard(ctx, format, input, qr, mark);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Failed to export card');

  const slug = input.vehicleLabel.replace(/\s+/g, '-').toLowerCase().slice(0, 40);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `bilt-${spec.label}-${slug}.png`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}
